// ConfigAgentsPage - Configuration des Agents IA (connecté au backend)
import { useState, useEffect } from "react";
import { Settings, Bot, Phone, Volume2, Save, RefreshCw, Loader2, Check, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ConfigAgentsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Config Audrey
  const [audreyConfig, setAudreyConfig] = useState(null);
  const [audreyStatus, setAudreyStatus] = useState(null);
  
  // Charger la config
  const loadConfig = async () => {
    setLoading(true);
    try {
      const [configRes, auditRes] = await Promise.all([
        axios.get(`${API}/api/audrey/config-raw`),
        axios.get(`${API}/api/audrey/audit`).catch(() => ({ data: null }))
      ]);
      // La config est dans content du fichier JSON
      const rawConfig = configRes.data?.content;
      if (rawConfig) {
        setAudreyConfig(typeof rawConfig === 'string' ? JSON.parse(rawConfig) : rawConfig);
      } else {
        setAudreyConfig(configRes.data);
      }
      setAudreyStatus(auditRes.data);
      setHasChanges(false);
    } catch (err) {
      console.error("Erreur chargement config:", err);
      toast.error("Erreur de chargement de la configuration");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  // Mettre à jour un champ
  const updateField = (path, value) => {
    setHasChanges(true);
    setAudreyConfig(prev => {
      const newConfig = { ...prev };
      const keys = path.split('.');
      let current = newConfig;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newConfig;
    });
  };

  // Sauvegarder
  const handleSave = async () => {
    setSaving(true);
    try {
      // Sauvegarder via l'endpoint de config
      await axios.post(`${API}/api/audrey/config-raw`, audreyConfig);
      toast.success("Configuration Audrey sauvegardée");
      setHasChanges(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  if (!audreyConfig) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertTriangle className="w-12 h-12 text-amber-400 mb-4" />
        <p className="text-zinc-400">Configuration Audrey non disponible</p>
        <Button onClick={loadConfig} className="mt-4">Réessayer</Button>
      </div>
    );
  }

  const identity = audreyConfig.identity || {};
  const scripts = audreyConfig.scripts || {};
  const escalation = audreyConfig.escalation_rules || {};

  return (
    <div className="space-y-6" data-testid="config-agents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Bot className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Configuration des Agents</h1>
            <p className="text-sm text-zinc-400">Paramétrage des agents IA vocaux et textuels</p>
          </div>
          {hasChanges && (
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
              Modifications non sauvegardées
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadConfig}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving || !hasChanges} 
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Sauvegarder
          </Button>
        </div>
      </div>

      {/* Status Bar */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${identity.status === 'active' ? 'bg-green-400 animate-pulse' : 'bg-zinc-600'}`} />
                <span className="text-sm text-zinc-400">Agent {identity.name || 'Audrey'}</span>
              </div>
              <div className="text-sm">
                <span className="text-zinc-500">Version:</span>
                <span className="ml-2 text-white">{identity.version || "N/A"}</span>
              </div>
              <div className="text-sm">
                <span className="text-zinc-500">Statut:</span>
                <span className={`ml-2 ${identity.status === 'active' ? 'text-green-400' : 'text-zinc-400'}`}>
                  {identity.status === 'active' ? 'Actif' : 'Inactif'}
                </span>
              </div>
              <div className="text-sm">
                <span className="text-zinc-500">Dernière mise à jour:</span>
                <span className="ml-2 text-white">
                  {audreyConfig.last_updated ? new Date(audreyConfig.last_updated).toLocaleString('fr-FR') : "N/A"}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="identity" className="space-y-6">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="identity">
            <Phone className="w-4 h-4 mr-2" />
            Identité
          </TabsTrigger>
          <TabsTrigger value="behavior">
            <Settings className="w-4 h-4 mr-2" />
            Comportement
          </TabsTrigger>
          <TabsTrigger value="tts">
            <Volume2 className="w-4 h-4 mr-2" />
            Voix (TTS)
          </TabsTrigger>
        </TabsList>

        {/* Identité */}
        <TabsContent value="identity" className="space-y-4">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Phone className="w-5 h-5 text-violet-400" />
                Identité de l'Agent
              </CardTitle>
              <CardDescription>Nom, rôle et présentation de l'agent vocal</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom de l'agent</Label>
                  <Input 
                    value={identity.name || "Audrey"} 
                    onChange={(e) => updateField('identity.name', e.target.value)}
                    className="bg-zinc-800 border-zinc-700" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Version</Label>
                  <Input 
                    value={identity.version || ""} 
                    disabled
                    className="bg-zinc-800 border-zinc-700 opacity-50" 
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Nom affiché</Label>
                <Input 
                  value={identity.display_name || ""} 
                  onChange={(e) => updateField('identity.display_name', e.target.value)}
                  className="bg-zinc-800 border-zinc-700" 
                />
              </div>
              <div className="space-y-2">
                <Label>Rôle</Label>
                <Input 
                  value={identity.rôle || "assistante virtuelle"} 
                  onChange={(e) => updateField('identity.rôle', e.target.value)}
                  className="bg-zinc-800 border-zinc-700" 
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <textarea 
                  value={identity.description || ""} 
                  onChange={(e) => updateField('identity.description', e.target.value)}
                  className="w-full h-24 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-white resize-none"
                  placeholder="Description de l'agent..."
                />
              </div>
              
              {/* Horaires */}
              <div className="p-4 bg-zinc-800 rounded-lg space-y-3">
                <p className="font-medium text-white">Horaires de disponibilité</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Ouverture</Label>
                    <Input 
                      value={identity.active_hours?.start || "00:00"} 
                      onChange={(e) => updateField('identity.active_hours.start', e.target.value)}
                      className="bg-zinc-700 border-zinc-600" 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Fermeture</Label>
                    <Input 
                      value={identity.active_hours?.end || "23:59"} 
                      onChange={(e) => updateField('identity.active_hours.end', e.target.value)}
                      className="bg-zinc-700 border-zinc-600" 
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Comportement */}
        <TabsContent value="behavior" className="space-y-4">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Settings className="w-5 h-5 text-blue-400" />
                Permissions & Comportement
              </CardTitle>
              <CardDescription>Capacités et règles d'escalade de l'agent</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
                <div>
                  <p className="font-medium text-white">Envoi de SMS</p>
                  <p className="text-sm text-zinc-400">L'agent peut envoyer des SMS aux appelants</p>
                </div>
                <Switch 
                  checked={identity.permissions?.can_send_sms !== false}
                  onCheckedChange={(v) => updateField('identity.permissions.can_send_sms', v)}
                />
              </div>
              <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
                <div>
                  <p className="font-medium text-white">Transfert d'appel</p>
                  <p className="text-sm text-zinc-400">L'agent peut transférer vers un humain</p>
                </div>
                <Switch 
                  checked={identity.permissions?.can_transfer_call !== false}
                  onCheckedChange={(v) => updateField('identity.permissions.can_transfer_call', v)}
                />
              </div>
              <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
                <div>
                  <p className="font-medium text-white">Collecte de données</p>
                  <p className="text-sm text-zinc-400">L'agent peut collecter des informations</p>
                </div>
                <Switch 
                  checked={identity.permissions?.can_collect_data !== false}
                  onCheckedChange={(v) => updateField('identity.permissions.can_collect_data', v)}
                />
              </div>
              
              {/* Escalade */}
              <div className="p-4 bg-zinc-800 rounded-lg space-y-3">
                <p className="font-medium text-white">Règles d'escalade</p>
                <div className="text-sm text-zinc-400 space-y-2">
                  {escalation.urgent_keywords && (
                    <div>
                      <span className="text-zinc-500">Mots-clés urgents:</span>
                      <span className="ml-2 text-amber-400">
                        {escalation.urgent_keywords?.join(", ") || "Non définis"}
                      </span>
                    </div>
                  )}
                  {escalation.business_hours && (
                    <div>
                      <span className="text-zinc-500">Heures ouvrées:</span>
                      <span className="ml-2 text-white">
                        {escalation.business_hours?.start} - {escalation.business_hours?.end}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TTS */}
        <TabsContent value="tts" className="space-y-4">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Volume2 className="w-5 h-5 text-amber-400" />
                Configuration Vocale (TTS)
              </CardTitle>
              <CardDescription>Paramètres de synthèse vocale ElevenLabs</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fournisseur</Label>
                  <Input 
                    value="ElevenLabs" 
                    disabled
                    className="bg-zinc-800 border-zinc-700 opacity-50" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Voice ID</Label>
                  <Input 
                    value={audreyConfig.tts?.voice_id || "EXAVITQu4vr4xnSDxMaL"} 
                    onChange={(e) => updateField('tts.voice_id', e.target.value)}
                    className="bg-zinc-800 border-zinc-700" 
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Modèle</Label>
                <Input 
                  value={audreyConfig.tts?.model || "eleven_multilingual_v2"} 
                  onChange={(e) => updateField('tts.model', e.target.value)}
                  className="bg-zinc-800 border-zinc-700" 
                />
              </div>
              
              {/* Quota Info */}
              <div className="p-4 bg-zinc-800 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-zinc-400">Quota ElevenLabs</span>
                  <Badge className="bg-emerald-500/20 text-emerald-400">Actif</Badge>
                </div>
                <p className="text-xs text-zinc-500">
                  Le quota est vérifié automatiquement. Une alerte est générée si le quota descend sous 5000 crédits.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
